import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Badge } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../../config/api';

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDateLabel(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === 2) return 'Day Before';
  return d.toLocaleDateString();
}

function getDateButtons() {
  const dates = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(d);
  }
  return dates;
}

export default function WorkLogWidget() {
  const { apiCall } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeEntries, setTimeEntries] = useState([]);
  const [workAccomplished, setWorkAccomplished] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadLog = async (date) => {
    try {
      setLoading(true);
      const dateStr = formatDate(date);
      const response = await apiCall(`${API_BASE_URL}/work-logs?date=${dateStr}&own=1`);
      const logs = response.data || [];
      if (logs.length > 0) {
        setTimeEntries(logs[0].time_entries || []);
        setWorkAccomplished(logs[0].work_accomplished || '');
      } else {
        setTimeEntries([]);
        setWorkAccomplished('');
      }
    } catch (error) {
      console.error('Failed to load work log:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLog(selectedDate);
  }, [selectedDate]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSaved(false);
  };

  const addTimeEntry = () => {
    setTimeEntries([...timeEntries, { start: '09:00', end: '17:00' }]);
    setSaved(false);
  };

  const removeTimeEntry = (index) => {
    setTimeEntries(timeEntries.filter((_, i) => i !== index));
    setSaved(false);
  };

  const updateTimeEntry = (index, field, value) => {
    const updated = [...timeEntries];
    updated[index] = { ...updated[index], [field]: value };
    setTimeEntries(updated);
    setSaved(false);
  };

  const calculateTotalHours = () => {
    let total = 0;
    for (const entry of timeEntries) {
      const [sh, sm] = entry.start.split(':').map(Number);
      const [eh, em] = entry.end.split(':').map(Number);
      total += (eh * 60 + em - sh * 60 - sm) / 60;
    }
    return Math.max(0, total).toFixed(1);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiCall(`${API_BASE_URL}/work-logs`, {
        method: 'POST',
        body: JSON.stringify({
          log_date: formatDate(selectedDate),
          time_entries: timeEntries,
          work_accomplished: workAccomplished,
        }),
      });
      setSaved(true);
    } catch (error) {
      console.error('Failed to save work log:', error);
    } finally {
      setSaving(false);
    }
  };

  const dateButtons = getDateButtons();

  return (
    <Card className="shadow-sm mb-0">
      <Card.Header className="text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f97316' }}>
        <div>
          <i className="fas fa-clock me-2"></i>
          Work Log
        </div>
        {timeEntries.length > 0 && (
          <Badge bg="light" text="dark">{calculateTotalHours()} hrs</Badge>
        )}
      </Card.Header>
      <Card.Body>
        {/* Date selector */}
        <div className="d-flex gap-2 mb-3">
          {dateButtons.map((date) => (
            <Button
              key={date.toISOString()}
              variant={formatDate(date) === formatDate(selectedDate) ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleDateSelect(date)}
            >
              {getDateLabel(date)}
              <br />
              <small>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</small>
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm" role="status" />
          </div>
        ) : (
          <>
            {/* Time entries */}
            {timeEntries.map((entry, i) => (
              <div key={i} className="d-flex align-items-center gap-2 mb-2">
                <Form.Control
                  type="time"
                  size="sm"
                  value={entry.start}
                  onChange={(e) => updateTimeEntry(i, 'start', e.target.value)}
                  style={{ width: 120 }}
                />
                <span>-</span>
                <Form.Control
                  type="time"
                  size="sm"
                  value={entry.end}
                  onChange={(e) => updateTimeEntry(i, 'end', e.target.value)}
                  style={{ width: 120 }}
                />
                <Button variant="outline-danger" size="sm" onClick={() => removeTimeEntry(i)}>
                  &times;
                </Button>
              </div>
            ))}

            <Button variant="outline-secondary" size="sm" onClick={addTimeEntry} className="mb-3">
              + Add Time Block
            </Button>

            {/* Work accomplished */}
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Work accomplished..."
              value={workAccomplished}
              onChange={(e) => { setWorkAccomplished(e.target.value); setSaved(false); }}
              className="mb-3"
              size="sm"
            />

            <Button
              variant={saved ? 'success' : 'primary'}
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
